import api from 'tw-oi-core/services/ContentDelivery'
import { getConfig } from 'tw-oi-core'
import { getBrandYearModelTypeSubtypeKey } from 'tw-oi-core/utils/data'

import { UI } from '../config'
import { cacheHeroShotUrl, getAllOfflineVehicleRecords, openHeroShotCache } from './offlineStorage'

function normalizeHeroShots(heroShots) {
  if (!heroShots) return []
  if (heroShots.toJS) return heroShots.toJS()
  if (Array.isArray(heroShots)) return heroShots
  return []
}

function filterHeroShotsForVehicle(heroShots, productType, productSubtype) {
  return heroShots.filter(heroShot =>
    heroShot.vehicles &&
    heroShot.vehicles.some(vehicle =>
      (productType ? vehicle.FOREST_RIVER_PRODUCT_TYPE === productType : true) &&
      (productSubtype ? vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE === productSubtype : true)
    )
  )
}

function getHeroShotUrls(heroShot) {
  const file = heroShot?.file
  if (!file) return null

  const smallUrl = file.variants?.small?.url || file.url
  const mediumUrl = file.variants?.medium?.url || file.url

  if (!smallUrl && !mediumUrl) return null

  return {
    small: smallUrl || mediumUrl,
    medium: mediumUrl || smallUrl,
  }
}

async function fetchHeroShotsForVehicle(vehicleInfo) {
  const { make, year, model, FOREST_RIVER_PRODUCT_TYPE, FOREST_RIVER_PRODUCT_SUB_TYPE } = vehicleInfo
  const { DEFAULT_LOCALE } = getConfig()
  const response = await api.getHeroShots(make, year, model, DEFAULT_LOCALE)
  return filterHeroShotsForVehicle(
    response.data || [],
    FOREST_RIVER_PRODUCT_TYPE,
    FOREST_RIVER_PRODUCT_SUB_TYPE
  )
}

/**
 * Downloads and caches a vehicle hero shot for offline display
 *
 * @param {object} vehicleInfo vehicle metadata
 * @param {Array|object} [contentsHeroShots] hero shots from Redux contents
 * @returns {Promise<object|null>} cached hero shot metadata
 */
export async function downloadVehicleHeroShot(vehicleInfo, contentsHeroShots = null) {
  if (!vehicleInfo) return null

  const { make, year, model, FOREST_RIVER_PRODUCT_TYPE, FOREST_RIVER_PRODUCT_SUB_TYPE } = vehicleInfo
  let heroShots = filterHeroShotsForVehicle(
    normalizeHeroShots(contentsHeroShots),
    FOREST_RIVER_PRODUCT_TYPE,
    FOREST_RIVER_PRODUCT_SUB_TYPE
  )

  if (!heroShots.length) {
    try {
      heroShots = await fetchHeroShotsForVehicle(vehicleInfo)
    } catch (error) {
      console.warn('[offline] Failed to fetch hero shot:', error)
      return null
    }
  }

  if (!heroShots.length) return null

  const urls = getHeroShotUrls(heroShots[0])
  if (!urls) return null

  const uniqueUrls = [...new Set([urls.small, urls.medium].filter(Boolean))]
  await Promise.all(uniqueUrls.map(url => cacheHeroShotUrl(url)))

  return {
    heroShotKey: getBrandYearModelTypeSubtypeKey(
      make,
      year,
      model,
      FOREST_RIVER_PRODUCT_TYPE,
      FOREST_RIVER_PRODUCT_SUB_TYPE
    ),
    urls,
  }
}

/**
 * Builds a heroShotMap from offline vehicle metadata
 *
 * @param {string} imageSize UI.HERO_SHOT_SMALL_SIZE or UI.HERO_SHOT_MEDIUM_SIZE
 * @returns {Promise<object>}
 */
export async function buildOfflineHeroShotMap(imageSize = UI.HERO_SHOT_SMALL_SIZE) {
  const records = await getAllOfflineVehicleRecords()
  const map = {}
  const useMedium = imageSize === UI.HERO_SHOT_MEDIUM_SIZE
  const cache = await openHeroShotCache()

  for (const record of records) {
    if (!record.heroShot?.heroShotKey || !record.heroShot.urls) continue

    const url = useMedium ? record.heroShot.urls.medium : record.heroShot.urls.small
    if (!url) continue

    const cached = await cache.match(url)
    map[record.heroShot.heroShotKey] = cached
      ? URL.createObjectURL(await cached.blob())
      : url
  }

  return map
}

/**
 * Revokes blob URLs created for offline hero shots
 *
 * @param {object} heroShotMap map of hero shot URLs
 */
export function revokeOfflineHeroShotMap(heroShotMap = {}) {
  Object.values(heroShotMap).forEach(url => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  })
}
